import { type FC, type ChangeEvent } from 'react';
import { useIntl, defineMessages } from 'dibs-react-intl';
import classnames from 'classnames';
import { Input } from 'dibs-elements/exports/Input.tsx';
import { Link } from 'dibs-elements/exports/Link.tsx';
import SearchIcon from 'dibs-icons/exports/Search.ts';

import { type TrackEventEventArgument } from '../../../utils/tracking/searchBrowse/filterTracking.ts';

import dibsCss from 'dibs-css';
import styles from './SbSharedRefineMenuSearchInput.module.css';

const messages = defineMessages({
    buttonLabel: {
        id: 'sb.SbSharedRefineMenuSearchInput.buttonLabel',
        defaultMessage: 'Search',
    },
    formLabel: {
        id: 'sb.SbSharedRefineMenuSearchInput.formLabel',
        defaultMessage: `{filterName, select,
        creator {Creator}
        location {Locations}
        material {Materials}
        metal {Metals}
        origin {Places}
        stone {Stones}
        stonecut {Stone Cuts}
        other {}
    }`,
    },
});

type Props = {
    autoComplete?: string;
    className?: string;
    horizontalSpacing?: 'small' | 'medium';
    onInput: (event: ChangeEvent<HTMLInputElement>) => void;
    onSubmit: (event: TrackEventEventArgument) => void;
    placeholder: string;
    size?: 'medium' | 'large';
    value: string;
    ariaControls?: string;
    filterName?: string;
    onBlur?: () => void;
    onFocus?: () => void;
    disabled?: boolean;
};

export const SbSharedRefineMenuSearchInput: FC<Props> = ({
    autoComplete = 'off',
    className,
    horizontalSpacing = 'medium',
    onInput,
    onSubmit,
    placeholder,
    size = 'medium',
    value,
    ariaControls,
    filterName = '',
    onBlur,
    onFocus,
    disabled,
}) => {
    const intl = useIntl();
    const containerClass = classnames(className);

    return (
        <div className={containerClass}>
            <form
                onSubmit={onSubmit}
                role="search"
                aria-label={intl.formatMessage(messages.formLabel, {
                    filterName: filterName.replace('-', ''),
                })}
                action=""
                data-tn="refine-menu-select-input-form"
            >
                <Input
                    ariaControls={ariaControls}
                    onBlur={onBlur}
                    onFocus={onFocus}
                    type="search"
                    autoComplete={autoComplete}
                    autoCorrect={false}
                    dataTn="search-input"
                    disabled={disabled}
                    horizontalSpacing={horizontalSpacing}
                    onChange={onInput}
                    placeholder={placeholder}
                    size={size}
                    spellCheck={false}
                    value={value}
                    rightDecorator={
                        <Link
                            className={styles.searchIconWrapper}
                            ariaLabel={intl.formatMessage(messages.buttonLabel)}
                            onClick={onSubmit}
                        >
                            <span className={disabled ? dibsCss.fillIron : dibsCss.fillNoir}>
                                <SearchIcon variation="24" />
                            </span>
                        </Link>
                    }
                />
            </form>
        </div>
    );
};
